class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(`${eventTarget}:user-action`);

    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
        CartPerformance.measureFromMarker(`${eventTarget}:user-action`, cartPerformanceUpdateMarker);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() =>
              CartPerformance.measureFromEvent('note-update:user-action', event)
            );
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

;(() => {
  if (window.__abcNameTagNoteSyncV2) return;
  window.__abcNameTagNoteSyncV2 = true;

  const productMatch = "America's Boating Club Name Tag"; // substring match
  const marker = "Name Tag Details:";

  const clean = (v) => (v ?? "").toString().trim();

  function isCartPage() {
    const p = clean(location.pathname).toLowerCase();
    return p === "/cart" || p.endsWith("/cart") || p.includes("/cart");
  }

  function getNoteTextarea() {
    return (
      document.querySelector("#Cart-note") ||
      document.querySelector('textarea[name="note"]') ||
      document.querySelector("cart-note textarea")
    );
  }

  function stripManagedBlock(noteRaw) {
    const note = clean(noteRaw);
    if (!note) return "";
    const blocks = note.split(/\n{2,}/).map(clean).filter(Boolean);
    return blocks.filter((b) => !b.startsWith(marker)).join("\n\n");
  }

  function findNameTagDetailsEl() {
    const nameEls = Array.from(document.querySelectorAll(".cart-item__name"));
    const matchEl = nameEls.find((a) => clean(a.textContent).includes(productMatch));
    if (!matchEl) return null;
    return matchEl.closest(".cart-item__details") || matchEl.closest("td") || matchEl.parentElement;
  }

  function buildBlockFromDom(detailsEl) {
    if (!detailsEl) return "";

    const title = clean(detailsEl.querySelector(".cart-item__name")?.textContent);
    const dl = detailsEl.querySelector("dl");
    if (!dl) return "";

    const rows = Array.from(dl.querySelectorAll(".product-option"))
      .map((row) => {
        const dt = row.querySelector("dt");
        const dd = row.querySelector("dd");
        if (!dt || !dd) return null;

        const key = clean(dt.textContent).replace(/:\s*$/, "");
        if (!key) return null;

        const link = dd.querySelector("a[href]");
        const val = clean(link ? link.getAttribute("href") : dd.textContent);
        if (!val) return null;

        return { key, val };
      })
      .filter(Boolean);

    if (!rows.length) return "";

    const header = title ? `${marker}\nProduct: ${title}` : marker;
    const lines = rows.map((r) => `${r.key}: ${r.val}`).join("\n");
    return `${header}\n${lines}`;
  }

  async function setCartNoteOnServer(note) {
    const url = window.routes?.cart_update_url || "/cart/update.js";

    // JSON first
    try {
      const r = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ note }),
        cache: "no-store",
        keepalive: true,
      });
      if (r.ok) return true;
    } catch {}

    // form fallback
    try {
      const body = new URLSearchParams();
      body.set("note", note);

      const r = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
        body: body.toString(),
        cache: "no-store",
        keepalive: true,
      });
      return r.ok;
    } catch {}

    // beacon fallback (best effort)
    try {
      const body = new URLSearchParams({ note }).toString();
      const blob = new Blob([body], { type: "application/x-www-form-urlencoded; charset=UTF-8" });
      navigator.sendBeacon(url, blob);
      return true;
    } catch {}

    return false;
  }

  function setTextareaValue(note) {
    const ta = getNoteTextarea();
    if (!ta) return false;

    if (ta.value !== note) {
      ta.value = note;
      ta.dispatchEvent(new Event("input", { bubbles: true }));
      ta.dispatchEvent(new Event("change", { bubbles: true }));
    }
    return true;
  }

  let inFlight = false;

  async function syncNow() {
    if (!isCartPage()) return;
    if (inFlight) return;
    inFlight = true;

    try {
      const ta = getNoteTextarea();
      const existing = clean(ta ? ta.value : "");

      const detailsEl = findNameTagDetailsEl();

      // product NOT in cart -> remove our managed block
      if (!detailsEl) {
        const kept = stripManagedBlock(existing);
        if (kept !== existing) {
          setTextareaValue(kept);
          await setCartNoteOnServer(kept);
        }
        return;
      }

      // product in cart -> build managed block and append after non-managed notes
      const block = buildBlockFromDom(detailsEl);
      if (!block) return;

      const kept = stripManagedBlock(existing);
      const next = [kept, block].map(clean).filter(Boolean).join("\n\n");

      if (next !== existing) {
        setTextareaValue(next);
        await setCartNoteOnServer(next);
      }
    } finally {
      inFlight = false;
    }
  }

  function scheduleSync() {
    if (!isCartPage()) return;
    setTimeout(() => { try { syncNow(); } catch {} }, 50);
    setTimeout(() => { try { syncNow(); } catch {} }, 300);
    setTimeout(() => { try { syncNow(); } catch {} }, 900);
  }

  // --- CLEAR NOTE ON REMOVE CLICK (single handler) ---
  function clearManagedNoteImmediately() {
    const ta = getNoteTextarea();
    const existing = clean(ta ? ta.value : "");
    const kept = stripManagedBlock(existing);

    // clear UI instantly
    setTextareaValue(kept);

    // clear server instantly
    setCartNoteOnServer(kept);

    // Dawn re-renders sections after click; re-clear textarea after swap
    setTimeout(() => setTextareaValue(kept), 200);
    setTimeout(() => setTextareaValue(kept), 800);
  }

  function removedRowName(clickedEl) {
    const row =
      clickedEl.closest(".cart-item") ||
      clickedEl.closest('[id^="CartItem-"]') ||
      clickedEl.closest('[id^="CartDrawer-Item-"]') ||
      clickedEl.closest("tr");
    return clean(row?.querySelector(".cart-item__name")?.textContent || "");
  }

  document.addEventListener(
    "click",
    (e) => {
      const target =
        e.target.closest("cart-remove-button") ||
        e.target.closest('a[href*="/cart/change"][href*="quantity=0"]');
      if (!target) return;

      // only clear for THIS specific product
      const name = removedRowName(target);
      if (!name.includes(productMatch)) return;

      clearManagedNoteImmediately();

      // also run the normal sync after Dawn finishes rerendering
      scheduleSync();
    },
    true
  );

  // initial load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleSync, { once: true });
  } else {
    scheduleSync();
  }
  window.addEventListener("load", scheduleSync);
  window.addEventListener("pageshow", scheduleSync);

  // Dawn pubsub (if present)
  try {
    if (typeof subscribe === "function" && window.PUB_SUB_EVENTS?.cartUpdate) {
      subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => scheduleSync());
    }
  } catch {}
})();
